import { useMemo, useCallback } from "react";
import { MetricKeys } from "@/constants/metrics";
import { getCellNumber } from "@/utils/tokens/movers";
import type { TokenRow, MoveItem, WinKey } from "@/types/movers";

export function useMoversData(
  rowsFiltered: TokenRow[],
  allowedTickers: Set<string>,
  changeByTicker: Map<string, Record<string, unknown>>,
  rollupByTicker: Map<string, Record<string, unknown>>,
  range: WinKey
) {
  // Helper to extract token metadata
  const getTokenMetadata = useCallback((r: TokenRow) => {
    const ticker = String(r['tokenSymbol'] ?? '').toUpperCase();
    const name = String(r['tokenName'] ?? r['name'] ?? ticker);
    const logo = String((r['logoUrl'] ?? r['logo_url'] ?? '') as string);
    const marketCap = getCellNumber(r, 'market_cap') ?? getCellNumber(r, MetricKeys.MarketCap) ?? 0;
    return { ticker, name, logo, marketCap };
  }, []);

  // Build moves from changes data
  const buildMoves = useCallback((metricKey: string): { top: MoveItem[]; bottom: MoveItem[] } => {
    const key = `${metricKey}_${range}`;
    const items: MoveItem[] = [];

    for (const r of rowsFiltered) {
      const { ticker, name, logo, marketCap } = getTokenMetadata(r);
      if (!ticker || !allowedTickers.has(ticker)) continue;

      const changeRec = changeByTicker.get(ticker);
      const value = changeRec ? (changeRec[key] as number | null | undefined) : null;
      if (value == null || !Number.isFinite(Number(value))) continue;

      items.push({ ticker, name, logo, value: Number(value), marketCap });
    }

    const sorted = items.sort((a, b) => b.value - a.value);
    return {
      top: sorted.slice(0, 10),
      bottom: [...sorted].reverse().slice(0, 10),
    };
  }, [rowsFiltered, allowedTickers, changeByTicker, range, getTokenMetadata]);

  // Build ranks from rollups data
  const buildRanksFromRollups = useCallback((metricKey: string): { top: MoveItem[]; bottom: MoveItem[] } => {
    const items: MoveItem[] = [];

    for (const r of rowsFiltered) {
      const { ticker, name, logo, marketCap } = getTokenMetadata(r);
      if (!ticker || !allowedTickers.has(ticker)) continue;

      const rec = rollupByTicker.get(ticker);
      const value = rec
        ? (rec[`${metricKey}_1d`] as number | null | undefined) ?? (rec[`${metricKey}_7d`] as number | null | undefined)
        : null;
      if (value == null || !Number.isFinite(Number(value))) continue;

      items.push({ ticker, name, logo, value: Number(value), marketCap });
    }

    const sorted = items.sort((a, b) => b.value - a.value);
    return {
      top: sorted.slice(0, 10),
      bottom: [...sorted].reverse().slice(0, 10),
    };
  }, [rowsFiltered, allowedTickers, rollupByTicker, getTokenMetadata]);

  // Funding extremes from snapshot data
  const fundingExtremes = useCallback((fundingSnapshotKey: string): { top: MoveItem[]; bottom: MoveItem[] } => {
    const items: MoveItem[] = [];

    for (const r of rowsFiltered) {
      const { ticker, name, logo, marketCap } = getTokenMetadata(r);
      if (!ticker || !allowedTickers.has(ticker)) continue;

      const fr = getCellNumber(r, fundingSnapshotKey);
      if (fr == null) continue;

      items.push({ ticker, name, logo, value: fr, marketCap });
    }

    const sorted = items.sort((a, b) => b.value - a.value);
    return {
      top: sorted.slice(0, 10),
      bottom: [...sorted].reverse().slice(0, 10),
    };
  }, [rowsFiltered, allowedTickers, getTokenMetadata]);

  // Market breadth calculation
  const breadth = useMemo(() => {
    let adv = 0, dec = 0, flat = 0;
    const arr: number[] = [];

    for (const r of rowsFiltered) {
      const ticker = String(r['tokenSymbol'] ?? '').toUpperCase();
      if (!ticker || !allowedTickers.has(ticker)) continue;

      const changeRec = changeByTicker.get(ticker);
      const value = changeRec ? (changeRec[`${MetricKeys.PriceClose}_${range}`] as number | null | undefined) : null;
      if (value == null || !Number.isFinite(Number(value))) continue;

      const n = Number(value);
      arr.push(n);
      if (n > 0) adv++;
      else if (n < 0) dec++;
      else flat++;
    }

    const avg = arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
    const med = arr.length ? [...arr].sort((a, b) => a - b)[Math.floor(arr.length / 2)] : 0;

    return { adv, dec, flat, total: adv + dec + flat, avg, med };
  }, [rowsFiltered, allowedTickers, changeByTicker, range]);

  return {
    buildMoves,
    buildRanksFromRollups,
    fundingExtremes,
    breadth,
  };
}

